import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Radio, Plus, Pencil, Ban, Trash2, TrendingUp, TrendingDown, Sliders, Cpu, RefreshCw, Zap, CalendarClock, Copy } from "lucide-react";
import { fmtPrice, clsx } from "../lib/format";

const RISK_LABEL = { low: "Low", medium: "Medium", high: "High" };

function fmtDuration(sec) {
  const s = Number(sec) || 0;
  if (s % 3600 === 0 && s >= 3600) return `${s / 3600}h`;
  if (s % 60 === 0 && s >= 60) return `${s / 60}m`;
  return `${s}s`;
}

// Builds a clean text block an admin can paste into Telegram/WhatsApp/email.
function formatSignalText(s, siteName = "Fx Cloud") {
  const up = ["buy", "call"].includes(s.direction);
  const price = s.entry_price ?? s.current_price;
  const lines = [
    `${up ? "📈" : "📉"} ${siteName.toUpperCase()} SIGNAL`,
    `Pair: ${s.pair}`,
    `Direction: ${up ? "BUY / CALL" : "SELL / PUT"}`,
    price != null ? `Entry: $${fmtPrice(price)}` : null,
    s.confidence != null ? `Confidence: ${Math.round(s.confidence)}%` : null,
    s.signal_strength != null ? `Signal strength: ${Math.round(s.signal_strength)}%` : null,
    s.trend_strength != null ? `Trend strength: ${Math.round(s.trend_strength)}%` : null,
    s.expected_movement != null ? `Expected move: ${s.expected_movement}%` : null,
    `Duration: ${fmtDuration(s.duration)}`,
    s.risk_level ? `Risk: ${RISK_LABEL[s.risk_level] || s.risk_level}` : null,
    s.strategy ? `Strategy: ${s.strategy}` : null,
    s.expiry ? `Valid until: ${new Date(s.expiry).toLocaleString()}` : null,
    s.notes ? `Note: ${s.notes}` : null,
  ].filter(Boolean);
  return lines.join("\n");
}

async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    return true;
  } catch { return false; }
}

const BLANK = {
  symbol: "BTC/USDT", pair: "", direction: "buy", entry_price: "", duration: 60,
  expiry_minutes: 10, confidence: 85, signal_strength: 85, trend_strength: 70,
  expected_movement: 3, risk_level: "medium", strategy: "", notes: "",
};

const STATUS_CLS = {
  active: "text-pos bg-pos/15", expired: "text-fx-s3 bg-white/5", cancelled: "text-neg bg-neg/15",
};

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const TIMEZONES = [
  "UTC", "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Sao_Paulo",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Africa/Lagos", "Africa/Johannesburg",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo", "Australia/Sydney",
];

function Stat({ label, value, cls = "" }) {
  return (
    <div className="rounded-xl bg-fx-s1 border border-white/5 p-4">
      <div className={clsx("font-mono text-2xl font-bold", cls)}>{value}</div>
      <div className="text-xs text-fx-s3">{label}</div>
    </div>
  );
}

export default function AdminSignals() {
  const [signals, setSignals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [assets, setAssets] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/signals"); setSignals(data); } catch { toast.error("Failed to load signals"); }
    try { const { data } = await api.get("/admin/signals/analytics"); setAnalytics(data); } catch { /* */ }
    try { const { data } = await api.get("/market/assets"); setAssets(data || []); } catch { /* */ }
    try { const { data } = await api.get("/admin/signals-config"); setCfg(data); } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openNew = () => { setForm(BLANK); setEditId(null); setDlg(true); };
  const openEdit = (s) => {
    setForm({
      symbol: s.symbol, pair: s.pair || "", direction: s.direction, entry_price: s.entry_price ?? "",
      duration: s.duration, expiry_minutes: 10, confidence: s.confidence ?? "", signal_strength: s.signal_strength ?? "",
      trend_strength: s.trend_strength ?? "", expected_movement: s.expected_movement ?? "",
      risk_level: s.risk_level || "medium", strategy: s.strategy || "", notes: s.notes || "",
    });
    setEditId(s.id); setDlg(true);
  };

  const num = (v) => (v === "" || v === null ? null : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        symbol: form.symbol, pair: form.pair || form.symbol, direction: form.direction,
        entry_price: num(form.entry_price), duration: Number(form.duration),
        expiry_minutes: Number(form.expiry_minutes) || 10,
        confidence: num(form.confidence), signal_strength: num(form.signal_strength),
        trend_strength: num(form.trend_strength), expected_movement: num(form.expected_movement),
        risk_level: form.risk_level, strategy: form.strategy, notes: form.notes,
      };
      if (editId) { await api.put(`/admin/signals/${editId}`, payload); toast.success("Signal updated"); }
      else { await api.post("/admin/signals", payload); toast.success("Signal published — customers notified"); }
      setDlg(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => { try { await api.post(`/admin/signals/${id}/cancel`); toast.success("Signal cancelled"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/admin/signals/${id}`); toast.success("Signal deleted"); load(); } catch { toast.error("Failed"); } };
  const copy = async (s) => {
    const ok = await copyText(formatSignalText(s));
    if (ok) toast.success(`Signal for ${s.pair} copied — paste it to your users`);
    else toast.error("Could not copy. Select and copy manually.");
  };

  const saveCfg = async () => {
    try { const { data } = await api.put("/admin/signals-config", cfg); setCfg(data); toast.success("Signal parameters saved"); }
    catch { toast.error("Failed to save parameters"); }
  };
  const updCfg = (k, v) => setCfg((c) => ({ ...c, [k]: v === "" ? null : Number(v) }));
  const updEng = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleDay = (d) => setCfg((c) => {
    const cur = Array.isArray(c.schedule_days) ? c.schedule_days : [];
    return { ...c, schedule_days: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b) };
  });
  const saveEngine = async () => {
    try { const { data } = await api.put("/admin/signals-config", cfg); setCfg(data); toast.success("Auto Signal Engine settings saved"); }
    catch { toast.error("Failed to save engine settings"); }
  };
  const loadPreview = async () => {
    setPreviewing(true);
    try { const { data } = await api.get("/admin/signals/engine/preview"); setPreview(data); }
    catch { toast.error("Failed to load preview"); }
    finally { setPreviewing(false); }
  };

  const a = analytics;

  return (
    <div className="space-y-6" data-testid="admin-signals">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-brandblue" />
          <div>
            <h3 className="font-display text-xl font-bold">Signal Management</h3>
            <p className="text-xs text-fx-s3">Create signals and copy them to send to users manually. Not shown on any user dashboard. Independent from the Auto Trading Bots.</p>
          </div>
        </div>
        <Button onClick={openNew} data-testid="admin-signal-new" className="bg-brandblue hover:bg-blue-500 gap-1.5"><Plus className="h-4 w-4" /> New Signal</Button>
      </div>

      {/* Analytics */}
      {a && (
        <div className="space-y-3">
          <div className="text-xs font-mono uppercase tracking-widest text-fx-s3">Signal Statistics</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Total Signals" value={a.signals.total} />
            <Stat label="Active" value={a.signals.active} cls="text-pos" />
            <Stat label="Completed" value={a.signals.completed} />
            <Stat label="Cancelled" value={a.signals.cancelled} cls="text-neg" />
            <Stat label="Winning Signals" value={a.signals.winning} cls="text-pos" />
            <Stat label="Losing Signals" value={a.signals.losing} cls="text-neg" />
            <Stat label="Signal Accuracy" value={`${a.signals.accuracy}%`} />
            <Stat label="Avg Confidence" value={`${a.signals.avg_confidence}%`} />
          </div>
        </div>
      )}

      {/* Signals table */}
      <div className="overflow-x-auto rounded-xl border border-white/5">
        <table className="w-full text-sm">
          <thead className="bg-fx-s1 text-fx-s3 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Asset</th>
              <th className="text-left px-3 py-2">Direction</th>
              <th className="text-right px-3 py-2">Entry</th>
              <th className="text-right px-3 py-2">Conf.</th>
              <th className="text-right px-3 py-2">Dur.</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-center px-3 py-2">Result</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {signals.length === 0 && <tr><td colSpan={8} className="text-center text-fx-s3 py-8">No signals yet. Create one, then copy it to share with users.</td></tr>}
            {signals.map((s) => {
              const up = ["buy", "call"].includes(s.direction);
              return (
                <tr key={s.id} data-testid={`admin-signal-row-${s.id}`} className="border-t border-white/5">
                  <td className="px-3 py-2 font-mono">{s.pair}{(s.auto || s.strategy === "Auto Engine") && <span className="ml-1.5 text-[9px] text-brandblue bg-brandblue/15 px-1.5 py-0.5 rounded align-middle">AUTO</span>}</td>
                  <td className="px-3 py-2">
                    <span className={clsx("inline-flex items-center gap-1 font-mono text-xs font-bold", up ? "text-pos" : "text-neg")}>
                      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{up ? "BUY" : "SELL"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-fx-s3">{fmtPrice(s.entry_price)}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.confidence != null ? `${Math.round(s.confidence)}%` : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.duration}s</td>
                  <td className="px-3 py-2 text-center"><span className={clsx("text-[10px] font-mono px-2 py-0.5 rounded uppercase", STATUS_CLS[s.status] || "text-fx-s3 bg-white/5")}>{s.status}</span></td>
                  <td className="px-3 py-2 text-center">
                    {s.signal_result === "win" ? <span className="text-[10px] text-pos font-mono">WIN</span>
                      : s.signal_result === "loss" ? <span className="text-[10px] text-neg font-mono">LOSS</span>
                        : <span className="text-[10px] text-fx-s3 font-mono">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => copy(s)} data-testid={`admin-signal-copy-${s.id}`} className="h-7 w-7 grid place-items-center rounded hover:bg-brandblue/15 text-fx-s3 hover:text-brandblue" title="Copy to share"><Copy className="h-3.5 w-3.5" /></button>
                      <button onClick={() => openEdit(s)} data-testid={`admin-signal-edit-${s.id}`} className="h-7 w-7 grid place-items-center rounded hover:bg-white/10 text-fx-s3 hover:text-white" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                      {s.status === "active" && <button onClick={() => cancel(s.id)} data-testid={`admin-signal-cancel-${s.id}`} className="h-7 w-7 grid place-items-center rounded hover:bg-gold/15 text-fx-s3 hover:text-gold" title="Cancel"><Ban className="h-3.5 w-3.5" /></button>}
                      <button onClick={() => del(s.id)} data-testid={`admin-signal-del-${s.id}`} className="h-7 w-7 grid place-items-center rounded hover:bg-neg/15 text-fx-s3 hover:text-neg" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Auto Signal Engine */}
      {cfg && (
        <div className="rounded-xl bg-fx-s1 border border-white/10 p-4 space-y-4 max-w-3xl" data-testid="signal-engine">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <Cpu className="h-5 w-5 text-brandblue" />
              <div>
                <div className="text-sm font-semibold">Auto Signal Engine</div>
                <p className="text-[11px] text-fx-s3 max-w-md">Optional. Scans live market data and auto-publishes signals whenever the measured metrics meet every threshold below. It never trades for users and never touches any bot.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={clsx("text-xs font-semibold", cfg.auto_enabled ? "text-pos" : "text-fx-s3")}>{cfg.auto_enabled ? "ON" : "OFF"}</span>
              <Switch data-testid="engine-toggle" checked={!!cfg.auto_enabled} onCheckedChange={(v) => updEng("auto_enabled", v)} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Scan interval (sec)</Label><Input type="number" data-testid="engine-interval" value={cfg.auto_interval_sec ?? ""} onChange={(e) => updEng("auto_interval_sec", Number(e.target.value))} className="bg-fx-base border-white/10 font-mono h-9" /></div>
            <div className="space-y-1"><Label className="text-xs">Trade duration (sec)</Label><Input type="number" data-testid="engine-duration" value={cfg.auto_duration ?? ""} onChange={(e) => updEng("auto_duration", Number(e.target.value))} className="bg-fx-base border-white/10 font-mono h-9" /></div>
            <div className="space-y-1"><Label className="text-xs">Signal valid (min)</Label><Input type="number" data-testid="engine-expiry" value={cfg.auto_expiry_minutes ?? ""} onChange={(e) => updEng("auto_expiry_minutes", Number(e.target.value))} className="bg-fx-base border-white/10 font-mono h-9" /></div>
            <div className="space-y-1"><Label className="text-xs">Max active auto signals</Label><Input type="number" data-testid="engine-max" value={cfg.max_active_auto ?? ""} onChange={(e) => updEng("max_active_auto", Number(e.target.value))} className="bg-fx-base border-white/10 font-mono h-9" /></div>
            <div className="space-y-1"><Label className="text-xs">Per-asset cooldown (min)</Label><Input type="number" data-testid="engine-cooldown" value={cfg.cooldown_min ?? ""} onChange={(e) => updEng("cooldown_min", Number(e.target.value))} className="bg-fx-base border-white/10 font-mono h-9" /></div>
          </div>

          {/* Engine schedule */}
          <div className="rounded-lg bg-fx-base border border-white/5 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-gold" />
                <div className="text-sm font-semibold">Engine Schedule</div>
                <span className="text-[11px] text-fx-s3">run only on selected days & hours</span>
              </div>
              <Switch data-testid="schedule-toggle" checked={!!cfg.schedule_enabled} onCheckedChange={(v) => updEng("schedule_enabled", v)} />
            </div>
            {cfg.schedule_enabled && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Active days</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="schedule-days">
                    {DAYS.map(([lbl, d]) => {
                      const on = (cfg.schedule_days || []).includes(d);
                      return (
                        <button key={d} type="button" data-testid={`schedule-day-${d}`} onClick={() => toggleDay(d)}
                          className={clsx("px-2.5 py-1 rounded text-xs font-mono border transition-colors",
                            on ? "bg-brandblue text-white border-brandblue" : "bg-fx-s1 text-fx-s3 border-white/10 hover:text-white")}>
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Start time</Label><Input type="time" data-testid="schedule-start" value={cfg.schedule_start || "00:00"} onChange={(e) => updEng("schedule_start", e.target.value)} className="bg-fx-s1 border-white/10 font-mono h-9" /></div>
                  <div className="space-y-1"><Label className="text-xs">End time</Label><Input type="time" data-testid="schedule-end" value={cfg.schedule_end || "23:59"} onChange={(e) => updEng("schedule_end", e.target.value)} className="bg-fx-s1 border-white/10 font-mono h-9" /></div>
                  <div className="space-y-1">
                    <Label className="text-xs">Timezone</Label>
                    <Select value={cfg.schedule_tz || "UTC"} onValueChange={(v) => updEng("schedule_tz", v)}>
                      <SelectTrigger data-testid="schedule-tz" className="bg-fx-s1 border-white/10 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-fx-s2 border-white/10 text-white max-h-72">
                        {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[11px] text-fx-s3">Overnight windows are supported (e.g. 22:00 → 04:00). Outside these days/hours the engine pauses and generates no signals, even while enabled.</p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={saveEngine} data-testid="engine-save" className="bg-brandblue hover:bg-blue-500">Save Engine Settings</Button>
            <Button onClick={loadPreview} data-testid="engine-preview-btn" variant="outline" className="border-white/10 bg-fx-base gap-1.5">
              <RefreshCw className={clsx("h-4 w-4", previewing && "animate-spin")} /> Live Preview
            </Button>
          </div>

          {preview && (
            <div className="overflow-x-auto rounded-lg border border-white/5" data-testid="engine-preview">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-fx-base border-b border-white/5 text-[11px]">
                <span className={clsx("inline-flex items-center gap-1 font-semibold", preview.running_now ? "text-pos" : "text-gold")}>
                  <span className={clsx("h-1.5 w-1.5 rounded-full", preview.running_now ? "bg-pos" : "bg-gold")} />
                  {preview.running_now ? "Engine running now" : preview.enabled ? "Enabled · paused by schedule" : "Engine off"}
                </span>
                {cfg.schedule_enabled && <span className="text-fx-s3 ml-auto">Window {cfg.schedule_start}–{cfg.schedule_end} {cfg.schedule_tz} · {preview.schedule_active_now ? "within hours" : "outside hours"}</span>}
              </div>
              <table className="w-full text-xs">
                <thead className="bg-fx-base text-fx-s3 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-2 py-1.5">Asset</th>
                    <th className="text-left px-2 py-1.5">Dir</th>
                    <th className="text-right px-2 py-1.5">Conf</th>
                    <th className="text-right px-2 py-1.5">Trend</th>
                    <th className="text-right px-2 py-1.5">Mom</th>
                    <th className="text-right px-2 py-1.5">Mkt</th>
                    <th className="text-right px-2 py-1.5">Vol</th>
                    <th className="text-right px-2 py-1.5">Exp</th>
                    <th className="text-right px-2 py-1.5">SS</th>
                    <th className="text-center px-2 py-1.5">Would fire</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.assets.map((x) => (
                    <tr key={x.symbol} className="border-t border-white/5 font-mono">
                      <td className="px-2 py-1.5">{x.symbol}</td>
                      <td className={clsx("px-2 py-1.5 font-bold", ["buy", "call"].includes(x.direction) ? "text-pos" : "text-neg")}>{["buy", "call"].includes(x.direction) ? "BUY" : "SELL"}</td>
                      {["confidence", "trend_strength", "momentum", "market_strength", "volatility", "expected_movement", "signal_strength"].map((k) => (
                        <td key={k} className={clsx("px-2 py-1.5 text-right", x.checks[k] === false && "text-neg")}>{x[k]}</td>
                      ))}
                      <td className="px-2 py-1.5 text-center">
                        {x.would_generate
                          ? <span className="inline-flex items-center gap-1 text-pos"><Zap className="h-3 w-3" />YES</span>
                          : <span className="text-fx-s3">no</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-2 py-1.5 text-[10px] text-fx-s3 bg-fx-base border-t border-white/5">Red values fail the configured threshold. Metrics are live and shift with the market — this is a snapshot.</div>
            </div>
          )}
        </div>
      )}

      {/* Signal generation parameters */}
      {cfg && (
        <div className="rounded-xl bg-fx-s1 border border-white/10 p-4 space-y-3 max-w-3xl" data-testid="signal-config">
          <div className="flex items-center gap-2"><Sliders className="h-4 w-4 text-brandblue" /><div className="text-sm font-semibold">Signal Generation Parameters</div></div>
          <p className="text-[11px] text-fx-s3">These percentage thresholds document your signal-generation criteria. They do NOT influence or guarantee individual trade outcomes — demo settles on the demo price engine and real settles on actual market movement.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              ["confidence_threshold", "Confidence threshold %"],
              ["trend_strength", "Trend strength %"],
              ["momentum", "Momentum %"],
              ["market_strength", "Market strength %"],
              ["signal_strength", "Signal strength %"],
              ["volatility_min", "Volatility min %"],
              ["volatility_max", "Volatility max %"],
              ["expected_movement_min", "Exp. movement min %"],
              ["expected_movement_max", "Exp. movement max %"],
            ].map(([k, lbl]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs">{lbl}</Label>
                <Input type="number" data-testid={`sigcfg-${k}`} value={cfg[k] ?? ""} onChange={(e) => updCfg(k, e.target.value)} className="bg-fx-base border-white/10 font-mono h-9" />
              </div>
            ))}
          </div>
          <Button onClick={saveCfg} data-testid="signal-config-save" variant="outline" className="border-white/10 bg-fx-base">Save Parameters</Button>
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="signal-form-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Signal" : "Publish New Signal"}</DialogTitle>
            <DialogDescription className="text-fx-s3">Configure the signal, then use the Copy button to send it to your users manually (Telegram, WhatsApp, email).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Asset / Pair</Label>
              <Select value={form.symbol} onValueChange={(v) => upd({ symbol: v })}>
                <SelectTrigger data-testid="signal-symbol" className="bg-fx-base border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-fx-s2 border-white/10 text-white">
                  {(assets.length ? assets : [{ symbol: "BTC/USDT" }, { symbol: "ETH/USDT" }]).map((x) => <SelectItem key={x.symbol} value={x.symbol}>{x.symbol}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Direction</Label>
              <Select value={form.direction} onValueChange={(v) => upd({ direction: v })}>
                <SelectTrigger data-testid="signal-direction" className="bg-fx-base border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-fx-s2 border-white/10 text-white">
                  <SelectItem value="buy">BUY / CALL</SelectItem>
                  <SelectItem value="sell">SELL / PUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Entry price (blank = market)</Label><Input data-testid="signal-entry" type="number" value={form.entry_price} onChange={(e) => upd({ entry_price: e.target.value })} placeholder="Market price" className="bg-fx-base border-white/10 font-mono" /></div>
            <div className="space-y-1"><Label className="text-xs">Trade duration (sec)</Label><Input data-testid="signal-duration" type="number" value={form.duration} onChange={(e) => upd({ duration: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
            <div className="space-y-1"><Label className="text-xs">Valid for (minutes)</Label><Input data-testid="signal-expiry" type="number" value={form.expiry_minutes} onChange={(e) => upd({ expiry_minutes: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
            <div className="space-y-1">
              <Label className="text-xs">Risk level</Label>
              <Select value={form.risk_level} onValueChange={(v) => upd({ risk_level: v })}>
                <SelectTrigger data-testid="signal-risk" className="bg-fx-base border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-fx-s2 border-white/10 text-white">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Confidence %</Label><Input data-testid="signal-confidence" type="number" value={form.confidence} onChange={(e) => upd({ confidence: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
            <div className="space-y-1"><Label className="text-xs">Signal strength %</Label><Input data-testid="signal-strength" type="number" value={form.signal_strength} onChange={(e) => upd({ signal_strength: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
            <div className="space-y-1"><Label className="text-xs">Trend strength %</Label><Input data-testid="signal-trend" type="number" value={form.trend_strength} onChange={(e) => upd({ trend_strength: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
            <div className="space-y-1"><Label className="text-xs">Expected movement %</Label><Input data-testid="signal-movement" type="number" value={form.expected_movement} onChange={(e) => upd({ expected_movement: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
            <div className="space-y-1 col-span-2"><Label className="text-xs">Strategy</Label><Input data-testid="signal-strategy" value={form.strategy} onChange={(e) => upd({ strategy: e.target.value })} placeholder="e.g. Momentum breakout" className="bg-fx-base border-white/10" /></div>
            <div className="space-y-1 col-span-2"><Label className="text-xs">Notes (optional)</Label><Textarea data-testid="signal-notes" value={form.notes} onChange={(e) => upd({ notes: e.target.value })} className="bg-fx-base border-white/10 min-h-[60px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="border-white/10 bg-fx-base">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="signal-form-save" className="bg-brandblue hover:bg-blue-500">{editId ? "Save Changes" : "Publish Signal"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
